import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { AtlasPageView } from '@/components/Atlas/AtlasPageView';
import { DocsSidebar } from '@/components/CodebaseView/DocsSidebar';
import { LeftSidebarPortal } from '@/components/portals/LeftSidebarPortal';
import { TocProvider, useToc } from '@/contexts/TocContext';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { searchParam } from '@/utils/router';

const atlasPageSearchSchema = z.object({
  branchId: searchParam().optional(),
  versionId: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId/atlas/$')({
  component: ProvidedAtlasDetailPage,
  validateSearch: atlasPageSearchSchema,
});

function ProvidedAtlasDetailPage() {
  // The splat is the atlas page route verbatim (it contains slashes, e.g. `sections/012-slug.md`).
  const { _splat: pageRoute } = Route.useParams();

  return (
    // Key the TOC to the page route so it resets when navigating between atlas pages.
    <TocProvider key={pageRoute}>
      <div className="mx-auto flex w-full flex-col gap-y-6 pt-4 pb-4">
        <AtlasDetailPage pageRoute={pageRoute ?? ''} />
      </div>
    </TocProvider>
  );
}

interface AtlasDetailPageProps {
  pageRoute: string;
}

function AtlasDetailPage({ pageRoute }: AtlasDetailPageProps) {
  const { codebaseId, organizationName } = useCodebaseRoute();
  const { branchId, versionId } = Route.useSearch();
  const { editor, handleTabClick, tocItems } = useToc();

  return (
    <>
      <LeftSidebarPortal.Mount>
        <DocsSidebar
          backTo="/$organizationName/cb/$codebaseId/atlas"
          backParams={{
            organizationName,
            codebaseId,
          }}
          backSearch={
            branchId || versionId
              ? { ...(branchId ? { branchId } : {}), ...(versionId ? { versionId } : {}) }
              : undefined
          }
          tocItems={tocItems}
          onTocClick={handleTabClick}
          editor={editor}
          showExpandCollapseButton={false}
        />
      </LeftSidebarPortal.Mount>
      <AtlasPageView route={pageRoute} />
    </>
  );
}
